
import { useState, useEffect } from 'react';
import { collection, doc, getDocs, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import * as Sentry from '@sentry/react';

export const useAudit = (auditId) => {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!auditId) {
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'audits', auditId),
      (doc) => {
        if (doc.exists()) {
          setAudit({ id: doc.id, ...doc.data() });
        } else {
          setError('Audit not found');
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching audit:', err);
        setError(err.message);
        setLoading(false);
        Sentry.captureException(err);
      }
    );

    return unsubscribe;
  }, [auditId]);

  return { audit, loading, error };
};

export const useAuditHistory = (filters = {}) => {
  const { currentUser } = useAuth();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const fetchAudits = async () => {
      try {
        setLoading(true);
        
        let q = query(
          collection(db, 'audits'),
          where('userId', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );

        if (filters.limit) {
          q = query(q, limit(filters.limit));
        }

        const snapshot = await getDocs(q);
        const auditList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setAudits(auditList);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching audit history:', err);
        setError(err.message);
        setLoading(false);
        Sentry.captureException(err);
      }
    };

    fetchAudits();
  }, [currentUser, filters.limit]);

  return { audits, loading, error };
};
